/**
 * File: apps/qbench-cp/reactapp/src/App/AppShell.js
 *
 * Purpose: Wraps the application tree with the cross-cutting providers and chrome the customer
 *     portal needs before any page renders.
 *
 * Scope: Root-level provider composition for the customer portal.
 *
 * Tags: customer-portal
 *
 * Overview: Provides a React Query client, a styled-components ThemeProvider using the current
 *     theme from useTheme, a react-notifications container, and a Suspense boundary with a
 *     Loading fallback, then renders its children inside MainWrapper. Acts as the single place
 *     where these shell-level concerns are composed around the rest of the app.
 *
 * Exports: AppShell — default export, a component that wraps `children` with the shell providers.
 */

import { Suspense } from 'react';
import { NotificationContainer } from 'react-notifications';
import { QueryClientProvider } from 'react-query';
import Loading from 'shared/layout/Loading';
import MainWrapper from 'shared/layout/MainWrapper';
import { useTheme } from 'shared/theme/useTheme';
import { queryClient } from 'shared/utils/queryClient';
import { ThemeProvider } from 'styled-components';

export default function AppShell({ children }) {
  const { theme } = useTheme();
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <NotificationContainer />
        <Suspense fallback={<Loading />}>
          <MainWrapper>{children}</MainWrapper>
        </Suspense>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
